import { useCallback, useEffect, useRef, useState } from 'react';

const normalizeCodeValue = (value) => String(value || '').trim();
const normalizeTypeValue = (value, fallback = 'CUSTOMER') => String(value || fallback).trim() || fallback;

const useAccountCodeWorkflow = ({
  code,
  setCode,
  type = 'CUSTOMER',
  originalCode = '',
  originalType = '',
  accountId = null,
  isNew = false,
  enabled = true,
  getNextCode,
  checkCodeUsage,
  debounceMs = 600
}) => {
  const [codeChecking, setCodeChecking] = useState(false);
  const [isCodeTaken, setIsCodeTaken] = useState(false);
  const generationKeyRef = useRef('');
  const validationRequestRef = useRef(0);

  useEffect(() => {
    if (enabled) return undefined;

    // Modal / form kapandığında sonraki CREATE açılışını taze başlat.
    generationKeyRef.current = '';
    validationRequestRef.current += 1;
    setCodeChecking(false);
    setIsCodeTaken(false);

    return undefined;
  }, [enabled]);

  const refreshCode = useCallback(async () => {
    if (typeof getNextCode !== 'function' || typeof setCode !== 'function') {
      return null;
    }

    const nextCode = await getNextCode(normalizeTypeValue(type));
    if (nextCode) {
      setCode(nextCode);
    }

    return nextCode || null;
  }, [getNextCode, setCode, type]);

  useEffect(() => {
    if (!enabled || typeof getNextCode !== 'function' || typeof setCode !== 'function') return undefined;

    const normalizedType = normalizeTypeValue(type);
    const normalizedOriginalType = normalizeTypeValue(originalType, normalizedType);
    const normalizedCode = normalizeCodeValue(code);
    const normalizedOriginalCode = normalizeCodeValue(originalCode);
    const generationKey = isNew
      ? `new:${normalizedType}`
      : `${accountId || 'account'}:${normalizedOriginalType}->${normalizedType}`;

    if (!isNew && normalizedType === normalizedOriginalType && !normalizedCode && normalizedOriginalCode) {
      setCode(normalizedOriginalCode);
      return undefined;
    }

    const shouldGenerate = isNew
      ? !normalizedCode
      : normalizedType !== normalizedOriginalType && (
        !normalizedCode ||
        normalizedCode === normalizedOriginalCode ||
        generationKeyRef.current !== generationKey
      );

    if (!shouldGenerate) {
      if (!isNew && normalizedType === normalizedOriginalType) {
        generationKeyRef.current = '';
      }
      return undefined;
    }

    if (generationKeyRef.current === generationKey) return undefined;

    let active = true;

    const loadNextCode = async () => {
      const nextCode = await getNextCode(normalizedType);
      if (!active) return;

      generationKeyRef.current = generationKey;
      if (nextCode) {
        setCode(nextCode);
      }
    };

    void loadNextCode();

    return () => {
      active = false;
    };
  }, [accountId, code, enabled, getNextCode, isNew, originalCode, originalType, setCode, type]);

  useEffect(() => {
    if (!enabled || typeof checkCodeUsage !== 'function') return undefined;

    const normalizedCode = normalizeCodeValue(code);
    const normalizedOriginalCode = normalizeCodeValue(originalCode);
    const shouldValidate = Boolean(normalizedCode) && (isNew || normalizedCode !== normalizedOriginalCode);

    if (!shouldValidate) {
      setIsCodeTaken(false);
      setCodeChecking(false);
      return undefined;
    }

    let active = true;
    const timer = window.setTimeout(async () => {
      if (!active) return;
      setCodeChecking(true);
      const requestId = validationRequestRef.current + 1;
      validationRequestRef.current = requestId;

      try {
        const exists = await checkCodeUsage(normalizedCode, isNew ? null : accountId);
        if (active && validationRequestRef.current === requestId) {
          setIsCodeTaken(Boolean(exists));
        }
      } finally {
        if (active && validationRequestRef.current === requestId) {
          setCodeChecking(false);
        }
      }
    }, debounceMs);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [accountId, checkCodeUsage, code, debounceMs, enabled, isNew, originalCode]);

  return {
    codeChecking,
    isCodeTaken,
    isCodeAvailable: !isCodeTaken,
    refreshCode
  };
};

export default useAccountCodeWorkflow;
